import React, { useState } from 'react';
import { supabase, isConfigured } from '../services/supabase';
import { Mail, Loader2, ArrowRight, CheckCircle2, AlertCircle, X, Check, Coins, Chrome } from 'lucide-react';
import { APP_NAME } from '../constants';

interface LoginProps {
  onClose?: () => void;
  initialMode?: 'login' | 'signup';
}

const Login: React.FC<LoginProps> = ({ onClose, initialMode = 'login' }) => {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [website, setWebsite] = useState('');

  const handleGoogleSignIn = async () => {
    if (!acceptedTerms) {
      setError("Please accept the Terms and Conditions to continue.");
      return;
    }
    if (!isConfigured) {
      setError("System Error: Supabase connection not configured.");
      return;
    }
    setGoogleLoading(true);
    setError(null);
    try {
      if (mode === 'signup' && acceptedMarketing) {
        localStorage.setItem('pending_marketing_consent', 'true');
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) throw error;
    } catch (err: any) {
      console.error('Google Sign-In Error:', err);
      setError(err.message || 'Failed to sign in with Google');
      setGoogleLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    if (!acceptedTerms) {
      setError('Please accept the Terms and Conditions to continue.');
      setLoading(false);
      return;
    }
    if (website.trim()) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
      return;
    }
    if (!isConfigured) {
      setError('System Error: Supabase connection not configured.');
      setLoading(false);
      return;
    }
    try {
      const { error: err } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${window.location.origin}/auth/callback`, data: { signup_app: 'floorplan' } },
      });
      if (err) throw err;
      if (mode === 'signup') localStorage.setItem('pending_marketing_consent', acceptedMarketing ? 'true' : 'false');
      setSent(true);
    } catch (err: any) {
      if (err.message === 'Failed to fetch' || !navigator.onLine) setError('Connection failed. Please check your internet.');
      else if (err.status === 400) setError('Invalid email address or request.');
      else setError(err.message || 'Failed to send login link');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode((m) => (m === 'login' ? 'signup' : 'login'));
    setError(null);
    setSent(false);
    setAcceptedTerms(false);
    setAcceptedMarketing(false);
    setWebsite('');
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center p-6 w-full h-full">
        <div className="max-w-md w-full rounded-2xl p-8 text-center relative" style={{ backgroundColor: '#1c1c28', border: '1px solid #2e2e40' }}>
          {onClose && (
            <button onClick={onClose} className="absolute top-4 right-4" style={{ color: '#8888aa' }}>
              <X size={24} />
            </button>
          )}
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(34,197,94,0.2)', color: '#22c55e' }}>
            <CheckCircle2 size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#f0f0f5' }}>Check your email</h2>
          <p className="mb-6" style={{ color: '#8888aa' }}>
            We&apos;ve sent a magic link to <span className="font-medium" style={{ color: '#f0f0f5' }}>{email}</span>.
            <br />Click it in this same browser to {mode === 'signup' ? 'create your account' : 'log in'}.
          </p>
          <button onClick={() => setSent(false)} className="text-sm font-medium underline" style={{ color: '#FF4C4C' }}>
            Try a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-6 w-full h-full min-h-[550px]">
      <div className="max-w-md w-full rounded-2xl p-8 relative" style={{ backgroundColor: '#1c1c28', border: '1px solid #2e2e40' }}>
        {onClose && (
          <button onClick={onClose} className="absolute top-4 right-4" style={{ color: '#8888aa' }}>
            <X size={24} />
          </button>
        )}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <img src="/Floor Plan Logo.png" alt="Floor Plan" className="h-16 w-auto object-contain" />
          </div>
          {mode === 'signup' && (
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl mb-4" style={{ backgroundColor: 'rgba(255,76,76,0.15)', border: '1px solid rgba(255,76,76,0.3)' }}>
              <Coins size={18} style={{ color: '#FF4C4C' }} />
              <span className="text-sm font-semibold" style={{ color: '#FF4C4C' }}>10 Free Credits</span>
            </div>
          )}
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#f0f0f5' }}>{mode === 'signup' ? 'Create Account' : 'Welcome Back'}</h1>
          <p style={{ color: '#8888aa' }}>
            {mode === 'signup' ? `Sign up to get 10 free credits and start creating with ${APP_NAME}.` : `Sign in to access your ${APP_NAME} projects.`}
          </p>
        </div>

        <div className="mb-6 space-y-3 p-3 rounded-xl" style={{ backgroundColor: '#252532', border: '1px solid #2e2e40' }}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-1" />
            <span className="text-xs" style={{ color: '#8888aa' }}>
              I accept the <a href="https://rehub.studio/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#FF4C4C' }}>Terms</a> and <a href="https://rehub.studio/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#FF4C4C' }}>Privacy Policy</a>.
            </span>
          </label>
          {mode === 'signup' && (
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={acceptedMarketing} onChange={(e) => setAcceptedMarketing(e.target.checked)} className="mt-1" />
              <span className="text-xs" style={{ color: '#8888aa' }}>I agree to receive marketing (optional).</span>
            </label>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg mb-6" style={{ backgroundColor: 'rgba(239,68,68,0.2)', color: '#f87171' }}>
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={handleAuth} className="space-y-4">
          <div className="absolute -left-[9999px] overflow-hidden" aria-hidden="true">
            <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1" style={{ color: '#8888aa' }}>Email</label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#666' }} />
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full pl-10 pr-3 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4C4C]/50"
                style={{ backgroundColor: '#111118', border: '1px solid #2e2e40', color: '#f0f0f5' }}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading || googleLoading}
            className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-lg font-semibold disabled:opacity-50"
            style={{ backgroundColor: '#FF4C4C', color: '#fff' }}
          >
            {loading ? <Loader2 size={20} className="animate-spin" /> : <>{mode === 'signup' ? 'Create Account' : 'Sign In'} <ArrowRight size={18} /></>}
          </button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full" style={{ borderTop: '1px solid #2e2e40' }}></div></div>
          <div className="relative flex justify-center text-sm"><span className="px-3" style={{ backgroundColor: '#1c1c28', color: '#8888aa' }}>or continue with</span></div>
        </div>
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading || googleLoading}
          className="w-full flex items-center justify-center gap-3 py-3.5 px-4 bg-white hover:bg-gray-100 text-gray-800 rounded-lg font-semibold transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {googleLoading ? (
            <Loader2 size={20} className="animate-spin text-gray-600" />
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <div className="mt-6 pt-6 text-center" style={{ borderTop: '1px solid #2e2e40' }}>
          <p className="text-sm" style={{ color: '#8888aa' }}>{mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}</p>
          <button onClick={toggleMode} className="text-sm font-medium mt-1 underline" style={{ color: '#FF4C4C' }}>
            {mode === 'signup' ? 'Log in here' : 'Sign up for free'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
